<template>
  <DefaultCrudView
    @create-new="newStatus"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="statuses"
        class="elevation-1"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:item.tenant_id="{ item }">
          {{ getTenantTitle(item.tenant_id) }}
        </template>

        <template v-slot:item.status_type="{ item }">
          <v-chip
            :color="getStatusTypeColor(item.status_type)"
            class="black--text"
            small
          >
            {{ item.status_type }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="editStatus(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="managePools(item)"
              >
                mdi-view-sequential
              </v-icon>
            </template>
            <span>{{ $t('manage_pools') }}</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="deleteStatus(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>
        </template>

      </v-data-table>

      <DefaultModal :title="getModalTitle()" ref="modal" @submit="save">
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

      <!-- Pool Status Modal -->
      <PoolStatus
        ref="poolStatusModal"
        mode="status"
        :entityId="selectedStatusForPools ? (selectedStatusForPools.status_id || selectedStatusForPools.id) : null"
        :entityTitle="selectedStatusForPools ? selectedStatusForPools.status_title : ''"
      />

    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }
</style>

<script>
import { mapActions, mapGetters } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import PoolStatus from '@/views/job/poolstatus/PoolStatus.vue'

import AppActions from '@/store/app/actions-types'
import TenantActions from '@/store/core/tenant/actions-types'

import StatusAPI from '@/services/api/job/status'

export default {
  name: "Status",

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
    PoolStatus
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem',
      'getUserApplicationById'
    ]),

    breadcrumbs() {
      return [
        {
          text: this.$t('job_settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: {
            name: "JobSettings",
            params: { appId: this.$route.params.appId }
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('status'),
          prependIcon: 'mdi-flag',
        },
      ]
    },

    appInstance() {
      return this.getUserApplicationById(this.$route.params.appId)
    },
    appConfig() {
      if (!this.appInstance || !this.appInstance.config) return {}
      return this.parseApplicationConfig(this.appInstance.config)
    },
    appTenantId() {
      return (
        this.appConfig.tenant_id ||
        this.appConfig.tenantId ||
        (this.appConfig.tenant && (this.appConfig.tenant.id || this.appConfig.tenant.tenant_id)) ||
        null
      )
    },

    fields() {
      const fields = [
        {
          key: 'tenant_id',
          name: 'tenant_id',
          label: this.$t('tenant'),
          type: 'select',
          value: this.selectedStatus ? this.selectedStatus.tenant_id : (this.appTenantId || ''),
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          items: this.tenants.map(tenant => ({
            text: tenant.title || tenant.tenant_title,
            value: tenant.tenant_id
          })),
          itemText: 'text',
          itemValue: 'value',
          cols: 12
        },
        {
          key: 'status_title',
          name: 'status_title',
          label: this.$t('status_title'),
          type: 'text',
          value: this.selectedStatus ? this.selectedStatus.status_title : '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 6
        },
        {
          key: 'status_type',
          name: 'status_type',
          label: this.$t('status_type'),
          type: 'select',
          value: this.selectedStatus ? this.selectedStatus.status_type : '',
          items: this.statusTypes,
          itemText: 'text',
          itemValue: 'value',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 12
        }
      ]

      return fields
    }
  },

  data() {
    return {
      loading: true,
      statuses: [],
      options: {
        sortBy: ['status_title'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: "create_status",
          eventToEmit: "create-new",
        }
      ],
      tenants: [],
      selectedStatus: null,
      selectedStatusForPools: null,
      headers: [
        { text: this.$t('status_title'), value: 'status_title', sortable: true },
        { text: this.$t('tenant'), value: 'tenant_id', sortable: true },
        { text: this.$t('status_type'), value: 'status_type', sortable: true },
        { text: this.$t('actions'), value: 'actions', sortable: false, width: '180px' }
      ],
      statusTypes: [
        { text: 'Waiting', value: 'waiting' },
        { text: 'Doing', value: 'doing' },
        { text: 'Done', value: 'done' },
        { text: 'Canceled', value: 'canceled' }
      ]
    }
  },

  mounted() {
    this.selectedStatus = null

    this[TenantActions.GET_ALL_TENANTS]()
      .then((tenants) => {
        this.tenants = tenants.items
      })
  },

  watch: {
    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    parseApplicationConfig(string) {
      try {
        return JSON.parse(string)
      } catch (e) {
        return {}
      }
    },

    newStatus() {
      this.selectedStatus = null
      this.$refs.modal.openModal()
    },

    save() {
      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const formData = {}
      this.fields.forEach(field => {
        if (field.type === 'number') {
          formData[field.name] = field.value !== '' ? Number(field.value) : null
        } else {
          formData[field.name] = field.value
        }
      })

      if (!this.selectedStatus) {
        if (this.appTenantId && formData.tenant_id == null) formData.tenant_id = this.appTenantId
      } else {
        if (formData.tenant_id == null) formData.tenant_id = this.selectedStatus.tenant_id || this.appTenantId || null
      }

      let apiCall
      
      if (this.selectedStatus) {
        const statusId = this.selectedStatus.status_id || this.selectedStatus.id
        
        if (!statusId) {
          console.error('Status ID not found in selected status object:', this.selectedStatus)
          this.$refs.modal.errorMessage = 'Status ID not found'
          return
        }
        
        apiCall = StatusAPI.updateStatus(statusId, formData)
      } else {
        apiCall = StatusAPI.createStatus(formData)
      }

      apiCall
        .then(() => {
          const message = this.selectedStatus ? 'Status updated successfully' : 'Status created successfully'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          this.$refs.modal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || 'An error occurred'
          }
        })
    },

    reloadData() {
      let options = {...this.$route.query}

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)
      
      // Filter statuses by tenant from app config when available
      if (this.appTenantId) {
        options['filter[tenant]'] = this.appTenantId
      }
      
      this.loading = true
      StatusAPI.getAllStatuses(options)
        .then((result) => {
          this.statuses = result.data.items || []
          this.itemsLength = result.data.total || 0
          this.loading = false
        })
        .catch(() => this.loading = false)
    },

    editStatus(status) {
      this.selectedStatus = status
      this.$refs.modal.openModal()
    },

    deleteStatus(status) {
      if (confirm(this.$t('confirm_delete_status'))) {
        // Handle different possible ID property names
        const statusId = status.status_id || status.id
        
        if (!statusId) {
          console.error('Status ID not found in status object:', status)
          return
        }
        
        StatusAPI.deleteStatus(statusId)
          .then(() => {
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Status deleted successfully'))
            this.reloadData()
          })
          .catch((error) => {
            console.error('Error deleting status:', error)
          })
      }
    },

    getTenantTitle(id) {
      let tenant = this.tenants.find(f => f.tenant_id == id)
      return tenant ? tenant.title : ''
    },

    getModalTitle() {
      return this.selectedStatus ? this.$t('edit_status') : this.$t('new_status')
    },

    getStatusTypeColor(type) {
      const colors = {
        'waiting': 'grey lighten-1',    // light grey
        'doing': 'orange lighten-2',      // light orange
        'done': 'green lighten-2',       // light green
        'canceled': 'purple lighten-2'    // light purple
      }
      return colors[type] || '#E0E0E0'
    },

    managePools(status) {
      this.selectedStatusForPools = status
      this.$refs.poolStatusModal.openModal()
    }
  }
}
</script>
